import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const slotBackground = {
    you: 'bg-[var(--sam-person-you)]',
    '1': 'bg-[var(--sam-person-1)]',
    '2': 'bg-[var(--sam-person-2)]',
    '3': 'bg-[var(--sam-person-3)]',
    auto: 'bg-[var(--sam-person-auto)]',
};
const slotText = {
    you: 'text-[var(--sam-person-you)]',
    '1': 'text-[var(--sam-person-1)]',
    '2': 'text-[var(--sam-person-2)]',
    '3': 'text-[var(--sam-person-3)]',
    auto: 'text-[var(--sam-person-auto)]',
};
/** The `--sam-person-*` custom property for a slot, for edges and tints drawn in that colour. */
export function personColorVar(slot) {
    return `var(--sam-person-${slot})`;
}
/** Text in a person's colour (their name beside an avatar). */
export function personTextClassName(slot) {
    return slotText[String(slot)] ?? 'text-[var(--sam-person-1)]';
}
/**
 * A teammate's slot. The design asks for join order within a project; until the API exposes
 * one, a stable hash of the user id keeps each person's colour the same on every screen.
 */
export function personSlotFor(userId, viewerId) {
    if (!userId)
        return 'auto';
    if (viewerId && userId === viewerId)
        return 'you';
    let hash = 0;
    for (let i = 0; i < userId.length; i++)
        hash = (hash * 31 + userId.charCodeAt(i)) | 0;
    return ((Math.abs(hash) % 3) + 1);
}
export function initialsFor(name) {
    const cleaned = name?.trim();
    if (!cleaned)
        return '?';
    const parts = cleaned.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return `${first}${second}`.toUpperCase();
}
const sizeClasses = {
    14: 'h-3.5 w-3.5 text-[6.5px]',
    20: 'h-5 w-5 text-[8px] shadow-[0_0_0_2px_var(--sam-separator)]',
    26: 'h-[26px] w-[26px] text-[10px] shadow-[0_0_0_2px_var(--sam-separator)]',
    32: 'h-8 w-8 text-xs shadow-[0_0_0_2px_var(--sam-separator)]',
};
/**
 * A solid avatar in the person's colour with initials in `--sam-person-ink`. Never faded.
 * Not a control: an avatar that acts (a person filter) goes inside a 44px button, because the
 * 14px and 20px sizes are far below a touch target.
 */
export function PersonAvatar({ name, person: slot, size = 26, imageUrl, selected = false, decorative = false, className = '', ...props }) {
    const label = name?.trim() || 'Unknown person';
    return (_jsx("span", { ...props, role: decorative ? undefined : 'img', "aria-label": decorative ? undefined : label, "aria-hidden": decorative || undefined, title: label, "data-person": slot, className: `inline-grid shrink-0 select-none place-items-center overflow-hidden rounded-full font-bold leading-none text-[var(--sam-person-ink)] ${slotBackground[String(slot)]} ${sizeClasses[size]} ${selected ? `outline-[1.5px] outline-offset-2 outline-solid ${personTextClassName(slot)} outline-current` : ''} ${className}`, children: imageUrl ? (_jsx("img", { src: imageUrl, alt: "", className: "h-full w-full object-cover", draggable: false })) : (_jsx("span", { className: "text-[var(--sam-person-ink)]", children: initialsFor(name) })) }));
}
/** Overlapping avatars for "shared with". Names go in the label, not in each avatar. */
export function AvatarStack({ people, size = 20, max = 4, label, className = '', }) {
    const shown = people.slice(0, max);
    const extra = people.length - shown.length;
    return (_jsxs("span", { role: "img", "aria-label": label, title: label, className: `inline-flex items-center pl-[5px] ${className}`, children: [shown.map((person) => (_jsx(PersonAvatar, { name: person.name, person: person.slot, size: size, imageUrl: person.imageUrl, decorative: true, className: "-ml-[5px]" }, person.id))), extra > 0 && (_jsxs("span", { "aria-hidden": "true", className: "-ml-[5px] inline-grid h-5 min-w-5 place-items-center rounded-full bg-[var(--sam-chip-bg)] px-1 text-[9px] font-bold text-fg-muted shadow-[0_0_0_2px_var(--sam-separator)]", children: ["+", extra] }))] }));
}
//# sourceMappingURL=PersonAvatar.js.map